import type React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import DatabaseIcon from '@atlaskit/icon/core/database';

import { Ati } from '@atlassian/ari';

import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

import { buildCreationLink } from '../../CreateContentRouteComponent/index.ts';
import { useHybridLinkClick } from '../../CreateContentPopover/CreateContentPopoverItems/hybridLinkHelper.ts';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.page.database',
		defaultMessage: 'Database',
		description: 'title indicating that this option will create a database',
	},
});

export function useDatabaseItem({
	parentId,
	source,
	spaceKey,
	onClick,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();

	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: 'create-database-popover-item',
	});

	const createContentOptions = {
		contentType: 'database',
		parentPageId: parentId,
		withFallback: true,
		source,
		spaceKey,
	} as const;

	const createHref = buildCreationLink(createContentOptions);
	const hybridPageLinkClick = useHybridLinkClick(createHref);

	const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
		onClick('createDatabasePopoverItem');
		hybridPageLinkClick(e);
	};

	return {
		ati: Ati.parse('ati:cloud:confluence:database'),
		children: formatMessage(messages.title),
		onClick: enforceStorageLimit(handleClick),
		elemBefore: DatabaseIcon,
		...(!shouldBlockCreate && { href: createHref }),
		testId: `create-menu-${shouldBlockCreate ? 'button' : 'link'}-item`,
	};
}
